import { useState } from "react";
import { plans } from "../../data/plans-data";
import { Icon } from "@iconify/react";
import { submitForm } from "./utils/api";
import { isValidEmail, isValidUKPhone, getDOBRange, formatDate, isValidDOBRange } from "./utils/validation";

const GetStartedForm = ({ clientKey, formId, planData = {} }) => {
     const [fieldErrors, setFieldErrors] = useState({});
     const { minDOB, maxDOB } = getDOBRange();

     const formattedMinDOB = formatDate(minDOB);
     const formattedMaxDOB = formatDate(maxDOB);

     const [email, setEmail] = useState("");
     const [phone, setPhone] = useState("");

     const [course, setCourse] = useState(planData?.planId || "");
     const [classSize, setClassSize] = useState(planData?.population || "");
     const [frequency, setFrequency] = useState(planData?.frequency || "");

     const [parentFirstName, setParentFirstName] = useState("");
     const [parentSurname, setParentSurname] = useState("");
     const [addressLine1, setAddressLine1] = useState("");
     const [addressLine2, setAddressLine2] = useState("");
     const [city, setCity] = useState("");
     const [postcode, setPostcode] = useState("");

     const address = [addressLine1, addressLine2, city, postcode]
          .filter(Boolean)
          .join("\n");

     const [contactMethod, setContactMethod] = useState("");

     const [studentFirstName, setStudentFirstName] = useState("");
     const [studentSurname, setStudentSurname] = useState("");
     const [dateOfBirth, setDob] = useState("");
     const [school, setSchool] = useState("");
     const [yearGroup, setYearGroup] = useState("");

     const [sen, setSen] = useState("");
     const [senDetails, setSenDetails] = useState("");

     const [learningGoal, setLearningGoal] = useState("");

     const [loading, setLoading] = useState(false);
     const [success, setSuccess] = useState(null);
     const [error, setError] = useState(null);

     const validate = () => {
          const errors = {};
          const cleanEmail = email?.trim();

          if (cleanEmail && !isValidEmail(cleanEmail)) {
               errors.email = "Please enter a valid email address.";
          }

          if (phone && !isValidUKPhone(phone)) {
               errors.phone = "Please enter a valid UK phone number.";
          }

          if (!isValidDOBRange(dateOfBirth, minDOB, maxDOB)) {
               errors.dateOfBirth = "Student must be between ages 7 and 11.";
          }

          setFieldErrors(errors);
          return Object.keys(errors).length === 0;
     };

     const handleSubmit = async (e) => {
          e.preventDefault();

          setLoading(true);
          setSuccess(null);
          setError(null);

          if (!validate()) {
               setError("Please fix the errors above");
               setLoading(false);
               return;
          }

          try {
               const selectedPlan = Object.values(plans).find((p) =>
                    p.id.startsWith(course)
               );

               const response = await submitForm({
                    client: clientKey,
                    formid: formId,

                    email,
                    phone,

                    course: selectedPlan?.name || course,
                    classSize,
                    frequency,

                    parentFirstName,
                    parentSurname,
                    address,
                    contactMethod,

                    studentFirstName,
                    studentSurname,
                    dateOfBirth,
                    school,
                    yearGroup,

                    sen,
                    senDetails,

                    learningGoal,
               });

               if (response.status === 200) {
                    setSuccess("Your details have been submitted successfully!");
               } else {
                    setError("Something went wrong, please try again.");
               }
          } catch (err) {
               setError("Error occurred. Please try again.");
          } finally {
               setLoading(false);
          }
     };

     const uniquePlans = Object.values(plans).filter(
          (plan, index, self) =>
               index === self.findIndex((p) => p.id.split("-")[0] === plan.id.split("-")[0])
     );

     const coursePlans = Object.values(plans).filter(
          (plan) => plan.id.split("-")[0] === course
     );

     const availablePopulations = coursePlans.filter(
          (p, i, self) =>
               i === self.findIndex((x) => x.population === p.population)
     );

     const availableFrequencies = coursePlans.filter(
          (p, i, self) =>
               i === self.findIndex((x) => x.freq === p.freq)
     );

     return (
          <div className="form-container">
               <form onSubmit={handleSubmit} className="form-inner" noValidate>

                    <section className="sect1">
                         <h3>Course Details</h3>

                         <div className="sect">
                              <label>Course of interest</label>
                              <div className="padder">
                                   <select
                                        value={course}
                                        onChange={(e) => {
                                             setCourse(e.target.value);
                                             setClassSize("");
                                             setFrequency("");
                                        }}
                                   >
                                        <option value="">Select your preference</option>

                                        {uniquePlans.map((p) => (
                                             <option key={p.id} value={p.id.split("-")[0]}>
                                                  {p.name}
                                             </option>
                                        ))}
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>

                         <div className="sect">
                              <label>Preferred class size</label>
                              <div className="padder">
                                   <select
                                        value={classSize}
                                        onChange={(e) => setClassSize(e.target.value)}
                                   >
                                        <option value="">Select your preference</option>

                                        {availablePopulations.map((p) => (
                                             <option key={p.population} value={p.population}>
                                                  {p.population}
                                             </option>
                                        ))}
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>

                         <div className="sect">
                              <label>Preferred lesson frequency</label>
                              <div className="padder">
                                   <select
                                        value={frequency}
                                        onChange={(e) => setFrequency(e.target.value)}
                                   >
                                        <option value="">Select your preference</option>

                                        {availableFrequencies.map((f) => (
                                             <option key={f.freq} value={f.freq}>
                                                  {f.freq}
                                             </option>
                                        ))}
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>
                    </section>

                    {/* Parent Details */}

                    <section className="sect2">
                         <h3>Parent's Details</h3>

                         <div className="sect">
                              <label>Parent's first name</label>
                              <div className="padder">
                                   <input
                                        value={parentFirstName}
                                        onChange={(e) => setParentFirstName(e.target.value)}
                                        placeholder="enter parent's first name..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Parent's surname</label>
                              <div className="padder">
                                   <input
                                        value={parentSurname}
                                        onChange={(e) => setParentSurname(e.target.value)}
                                        placeholder="enter parent's last name..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Address</label>
                              <div className="padder">
                                   <input
                                        value={addressLine1}
                                        onChange={(e) => setAddressLine1(e.target.value)}
                                        placeholder="address line 1..."
                                   />
                              </div>
                              <div className="padder">
                                   <input
                                        value={addressLine2}
                                        onChange={(e) => setAddressLine2(e.target.value)}
                                        placeholder="address line 2..."
                                   />
                              </div>
                              <div className="padder">
                                   <input
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        placeholder="city..."
                                   />
                              </div>
                              <div className="padder">
                                   <input
                                        value={postcode}
                                        onChange={(e) => setPostcode(e.target.value)}
                                        placeholder="postcode..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Email</label>
                              <div className="padder">
                                   <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="enter parent's email..."
                                   />
                              </div>
                              {fieldErrors.email && (
                                   <span className="error-text">{fieldErrors.email}</span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Phone number</label>
                              <div className="padder">
                                   <input
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="enter parents phone number..."
                                   />
                              </div>
                              {fieldErrors.phone && (
                                   <span className="error-text">{fieldErrors.phone}</span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Preferred contact method</label>
                              <div className="padder">
                                   <select
                                        value={contactMethod}
                                        onChange={(e) => setContactMethod(e.target.value)}
                                   >
                                        <option value="">Select an option</option>
                                        <option>Email</option>
                                        <option>Phone</option>
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>
                    </section>

                    {/* Student Details */}

                    <section className="sect3">
                         <h3>Student's Details</h3>

                         <div className="sect">
                              <label>Student's first name</label>
                              <div className="padder">
                                   <input
                                        value={studentFirstName}
                                        onChange={(e) => setStudentFirstName(e.target.value)}
                                        placeholder="enter student's first name..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Student's surname</label>
                              <div className="padder">
                                   <input
                                        value={studentSurname}
                                        onChange={(e) => setStudentSurname(e.target.value)}
                                        placeholder="enter student's last name..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Date of birth</label>

                              <div className="padder">
                                   <input
                                        type="date"
                                        value={dateOfBirth}
                                        min={formattedMinDOB}
                                        max={formattedMaxDOB}
                                        onChange={(e) => setDob(e.target.value)}
                                        placeholder="choose a date..."
                                   />
                              </div>
                              {fieldErrors.dateOfBirth && (
                                   <span className="error-text">{fieldErrors.dateOfBirth}</span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Current school</label>
                              <div className="padder">
                                   <input
                                        value={school}
                                        onChange={(e) => setSchool(e.target.value)}
                                        placeholder="enter student's current school..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Current year group</label>
                              <div className="padder">
                                   <input
                                        value={yearGroup}
                                        onChange={(e) => setYearGroup(e.target.value)}
                                        placeholder="enter student's current year group..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Does the student have SEN?</label>
                              <div className="padder">
                                   <select value={sen} onChange={(e) => setSen(e.target.value)}>
                                        <option value="">Select an option</option>
                                        <option>Yes</option>
                                        <option>No</option>
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>

                         {sen === "Yes" && (
                              <div className="sect">
                                   <label>If yes, which?</label>
                                   <div className="padder">
                                        <textarea
                                             value={senDetails}
                                             onChange={(e) => setSenDetails(e.target.value)}
                                             placeholder="enter student's SEN..."
                                        />
                                   </div>
                              </div>
                         )}
                    </section>

                    {/* Learning Goals */}

                    <section className="sect1">
                         <h3>Learning Goals</h3>

                         <div className="sect">
                              <label>Reason for tuition</label>
                              <div className="padder">
                                   <select
                                        value={learningGoal}
                                        onChange={(e) => setLearningGoal(e.target.value)}
                                   >
                                        <option value="">Select an option</option>
                                        <option>Preparation for 11+ exams</option>
                                        <option>Strengthen Maths skills</option>
                                        <option>Strengthen English skills</option>
                                        <option>Improve reasoning skills</option>
                                        <option>General academic support</option>
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>
                    </section>

                    <button disabled={loading}>
                         {loading ? "Sending..." : "Submit"}
                    </button>

                    {success && <div className="success-message">{success}</div>}
                    {error && <div className="error-message">{error}</div>}
               </form>
          </div>
     );
};

export default GetStartedForm;